import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCirclePlus, faCircleMinus, faCheck, faClock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import '../styles/TransactionDetail.css';
import Loader from '../components/Loader';

const statusSteps = [
  { key: 'initiated', label: 'Initiated', icon: faClock },
  { key: 'pending', label: 'Verifying', icon: faClock },
  { key: 'processing', label: 'Processing', icon: faSpinner },
  { key: 'complete', label: 'Completed', icon: faCheck },
];


function TransactionDetail({ user }) {
  const { userId, index } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState(null);
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const fetchTransaction = async () => {
      if (!userId || index === undefined) {
        setLoading(false);
        return;
      }

      const parsedIndex = parseInt(index, 10);

      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const allTransactions = (userData.transactions || [])
            .map((t, idx) => ({ ...t, originalIndex: idx }));
          
          const tx = allTransactions.find(t => t.originalIndex === parsedIndex);
          if (tx) {
            setTransaction(tx);
            setType(tx.type);
            setAmount(tx.amount);
          }
        }
      } catch (error) {
        console.error('Error fetching transaction:', error);
      }

      setLoading(false);
    };

    fetchTransaction();
  }, [userId, index]);

  if (loading) {
    return <Loader />;
  }

  const status = transaction?.status?.toLowerCase() || 'complete';
  const stepMap = { initiated: 0, pending: 1, processing: 2, complete: 3 };
  const widthMap = { initiated: '5%', pending: '33%', processing: '66%', complete: '100%' };
  const currentStep = stepMap[status] ?? 3;
  const progressWidth = widthMap[status] ?? '100%';

  return (
    <div className="tx-detail-page">
      <div className="tx-detail-container">
        <button className="tx-detail-back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        <div className="tx-detail-card">
          <div className="tx-detail-header">
            <div className={`tx-detail-icon ${type || 'debit'}`}>
              <FontAwesomeIcon 
                icon={type === 'credit' ? faCirclePlus : faCircleMinus} 
              />
            </div>
            <div className="tx-detail-title">
              {transaction?.description || `Transaction`}
            </div>
            <div className={`tx-detail-amount ${type || 'debit'}`}>
              {type === 'credit' ? '+' : '-'}${amount?.toFixed(2) || '0.00'}
            </div>
          </div>

          <div className="tx-progress-bar">
            <div className="tx-progress-track">
              <div className="tx-progress-fill" style={{ width: progressWidth }}></div>
            </div>
            <div className="tx-progress-steps">
              {statusSteps.map((step, idx) => (
                <div 
                  key={step.key} 
                  className={`tx-progress-step ${idx <= currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
                >
                  <div className="tx-step-icon">
                    <FontAwesomeIcon icon={idx < currentStep ? faCheck : step.icon} />
                  </div>
                  <span className="tx-step-label">{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          {currentStep < 3 && (
            <div className="tx-progress-message">
              {transaction?.description || 'Transaction processing. Please wait...'}
            </div>
          )}

          <div className="tx-detail-info">
            <div className="tx-detail-row">
              <span className="tx-detail-label">Type</span>
              <span className="tx-detail-value">{type || 'debit'}</span>
            </div>
            <div className="tx-detail-row">
              <span className="tx-detail-label">Account</span>
              <span className="tx-detail-value">{transaction?.account || 'N/A'}</span>
            </div>
            {transaction?.sender && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">Sender</span>
                <span className="tx-detail-value">{transaction.sender}</span>
              </div>
            )}
            {transaction?.beneficiary && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">
                  {transaction?.account === "Investments" ? "Currency" : "Beneficiary"}
                </span>
                <span className="tx-detail-value">{transaction.beneficiary}</span>
              </div>
            )}
            {transaction?.beneficiaryAccount && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">
                  {transaction?.account === "Investments" ? "Wallet Address" : "Account Number"}
                </span>
                <span className="tx-detail-value">{transaction.beneficiaryAccount}</span>
              </div>
            )}
            {transaction?.bankName && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">Bank name</span>
                <span className="tx-detail-value">{transaction.bankName}</span>
              </div>
            )}
            {transaction?.date && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">Date</span>
                <span className="tx-detail-value">
                  {moment(transaction.date).format('MMMM D, YYYY')}
                </span>
              </div>
            )}
            <div className="tx-detail-row">
              <span className="tx-detail-label">Status</span>
              <span className="tx-detail-value">{transaction?.status || 'Complete'}</span>
            </div>

            {transaction?.explore && (
              <div className="tx-detail-row">
                <span className="tx-detail-label">Explorer</span>
                <span className="tx-detail-value">
                  <a
                    href={transaction.explore.startsWith("http") ? transaction.explore : `https://${transaction.explore}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#D52A2A", textDecoration: "none", fontWeight: "500" }}
                  >
                    View on Explorer
                  </a>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TransactionDetail;